import { Contact, ContactType } from '@api/generated/graphql';
import {
  ActionMenu,
  Button,
  Icon,
  Stack,
  Tag,
  Tooltip,
} from '@servicetitan/design-system';
import classNames from 'classnames';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useCopyToClipboard, useHover } from 'usehooks-ts';
import styles from './ContactMethodMenu.module.css';
import maskPhoneNumber from '@lib/utils/maskPhoneNumber';
import getContactMethodIconName from '@lib/utils/getContactMethodIconName';
import ContactMethodLink from '@components/ContactMethodLink/ContactMethodLink';
import { tokens } from '@servicetitan/tokens';
import ContactMethodsMenuContext from './context/ContactMethodsMenuContext';

interface ContactMethodMenuItemProps {
  contact: Contact;
}

const ContactMethodMenuItem = ({ contact }: ContactMethodMenuItemProps) => {
  const hoverRef = useRef(null);
  const [isCopied, setIsCopied] = useState(false);
  const isHover = useHover(hoverRef);
  const copy = useCopyToClipboard()[1];

  const handleCopy = () => {
    if (contact.type === ContactType.Email) {
      copy(contact.value);
    } else {
      copy(maskPhoneNumber(contact.value));
    }
    setIsCopied(true);
  };

  useEffect(() => {
    if (!isHover && isCopied) {
      setIsCopied(false);
    }
  }, [isHover, isCopied]);

  const tooltipText = () => {
    if (isCopied) {
      return 'Copied!';
    }
    if (isHover) {
      return 'Copy';
    }
    return undefined;
  };

  return (
    <Stack
      className={`${styles.contactMethodItem} w-100`}
      direction='row'
      alignItems='flex-start'
      ref={hoverRef}
      justifyContent='space-between'
    >
      <Stack spacing={1} alignItems='flex-start'>
        <Icon
          className='c-neutral-100 m-t-half'
          name={getContactMethodIconName(contact.type)}
          size='16px'
        />
        <Stack
          direction='column'
          className={classNames({
            [styles.contactMethodContentShort]: isHover,
            [styles.contactMethodContentLong]: !isHover,
          })}
        >
          <ContactMethodLink contact={contact} />
          {contact.memo && <Tag>{contact.memo}</Tag>}
        </Stack>
      </Stack>
      <div>
        <Tooltip text={tooltipText()} open={isCopied} placement='top'>
          {isCopied ? (
            <Icon name='check' size={20} color={tokens.colorGreen500} />
          ) : (
            <Button
              aria-label='Copy contact'
              className={classNames({ 'd-n': !isHover })}
              size='xsmall'
              fill='subtle'
              iconName={'content_copy'}
              onClick={handleCopy}
            />
          )}
        </Tooltip>
      </div>
    </Stack>
  );
};

interface ContactMethodsMenuProps {
  trigger: ReactNode;
  contacts: Contact[];
}

const ContactMethodsMenu = ({ trigger, contacts }: ContactMethodsMenuProps) => {
  const [isContactsMenuOpen, setIsContactsMenuOpen] = useState(false);

  // when the modal is open, the onClickOutside from the menu will cause the menu to close, preventing any interaction with the modal.
  // this state allows us to control whether the menu should close on outside click or not.
  const [shouldCloseOnOutsideClick, setShouldCloseOnOutsideClick] =
    useState(true);

  const handleOutsideClick = () => {
    if (shouldCloseOnOutsideClick) {
      setIsContactsMenuOpen(false);
    }
  };

  const memoizedMenuContext = useMemo(() => {
    return { setIsContactsMenuOpen, setShouldCloseOnOutsideClick };
  }, [setIsContactsMenuOpen, setShouldCloseOnOutsideClick]);

  return (
    <ContactMethodsMenuContext.Provider value={memoizedMenuContext}>
      <ActionMenu
        autoFlipVertically
        width='auto'
        direction='bl'
        open={isContactsMenuOpen}
        onClickOutside={handleOutsideClick}
        trigger={
          <div onClick={() => setIsContactsMenuOpen(true)}>{trigger}</div>
        }
      >
        <div className={styles.contactMethodMenu}>
          <Stack className={`p-x-2`} spacing={2} direction='column'>
            {contacts.map((contact) => (
              <Stack.Item key={contact.id} className='w-100'>
                <ContactMethodMenuItem contact={contact} />
              </Stack.Item>
            ))}
          </Stack>
        </div>
      </ActionMenu>
    </ContactMethodsMenuContext.Provider>
  );
};

export default ContactMethodsMenu;
